import { useParams, useNavigate } from 'react-router-dom';
import { useGetItemQuery, useDeleteItemMutation } from '../store/docApi';
import { Box, Typography, CircularProgress, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import Motifs from '../Motif/Motifs';
import Layouts from '../Layout/Layouts';
import Colorways from '../Colorway/Colorways';
import EditableProperty from '../components/widgets/EditableProperty';
import PublishItem from '../components/widgets/PublishItem';
import PictureManager from '../components/widgets/PictureManager';
import Patterns from '../Pattern/Patterns';
import Parts from '../Part/Parts';
import ItemMetadata from '../components/widgets/ItemMetadata';
import React from 'react';

export default function WorkspaceDetails() {
  const { pk, id } = useParams();
  const navigate = useNavigate();
  const { data: item, error, isLoading } = useGetItemQuery({ label: 'workspace', pk, id });
  const [deleteItem] = useDeleteItemMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" textAlign="center" sx={{ mt: 4 }}>
        Failed to load workspace details: {error.message}
      </Typography>
    );
  }

  if (!item) {
    return (
      <Typography textAlign="center" sx={{ mt: 4 }}>
        Workspace not found.
      </Typography>
    );
  }

  const handleDelete = async () => {
    await deleteItem({ pk, id });
    setDeleteDialogOpen(false);
    navigate('/workspaces');
  };

  return (
    <Box>
      <EditableProperty title="Name" item={item} propname="name" propvalue={item.name} placeholder="Enter workspace name..." />
      <EditableProperty
        title="Description"
        item={item}
        propname="description"
        propvalue={item.description}
        placeholder="Enter workspace description..."
        multiline={true}
      />
      {item.picture && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <img src={item.picture} alt={item.name} height={200} />
        </Box>
      )}
      <PictureManager item={item} />
      <Divider sx={{ my: 3 }} />
      <Motifs item={item} />
      <Divider sx={{ my: 3 }} />
      <Layouts item={item} />
      <Divider sx={{ my: 3 }} />
      <Colorways item={item} />
      <Divider sx={{ my: 3 }} />
      <Patterns item={item} />
      <Divider sx={{ my: 3 }} />
      <Parts item={item} />
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <PublishItem item={item} />
        <Button color="error" variant="outlined" onClick={() => setDeleteDialogOpen(true)}>
          Delete Workspace
        </Button>
      </Box>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Workspace</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this workspace? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
      <ItemMetadata item={item} />
    </Box>
  );
}
